import React, { useState, useEffect } from 'react';
import TripTypeSelector, { TripType } from '../ui/TripTypeSelector';
import ContactSharingPreferences from '../ui/ContactSharingPreferences';
import GooglePlacesAutocomplete from '../ui/GooglePlacesAutocomplete';
import { isValidPhone } from '../../constants/phone';

interface AmbulanceBookingData {
  tripType: TripType;
  originAddress: string;
  destinationAddress: string;
  originCoordinates?: { lat: number; lng: number };
  destinationCoordinates?: { lat: number; lng: number };
  accessNotes: string;
  onSiteContact: {
    name: string;
    phone: string;
    shareWithProvider: boolean;
  };
  ambulanceType?: string;
  specialRequests?: string;
}

interface PricingResult {
  basePrice: number;
  emergencySurge: number;
  total: number;
  breakdown: string;
  currency?: string;
}

interface AmbulanceBookingFormProps {
  caseId?: string;
  initialData?: Partial<AmbulanceBookingData>;
  onSubmit: (data: AmbulanceBookingData, pricing: PricingResult) => void;
  onCancel: () => void;
  disabled?: boolean;
}

const AmbulanceBookingForm: React.FC<AmbulanceBookingFormProps> = ({
  caseId,
  initialData,
  onSubmit,
  onCancel,
  disabled = false,
}) => {
  const [formData, setFormData] = useState<AmbulanceBookingData>({
    tripType: initialData?.tripType || 'REGULAR',
    originAddress: initialData?.originAddress || '',
    destinationAddress: initialData?.destinationAddress || '',
    originCoordinates: initialData?.originCoordinates,
    destinationCoordinates: initialData?.destinationCoordinates,
    accessNotes: initialData?.accessNotes || '',
    onSiteContact: initialData?.onSiteContact || {
      name: '',
      phone: '',
      shareWithProvider: true,
    },
    ambulanceType: 'BASIC', // Always default to Basic Ambulance for now
    specialRequests: initialData?.specialRequests || '',
  });

  const [pricing, setPricing] = useState<PricingResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Calculate pricing when trip type changes
  useEffect(() => {
    calculatePricing();
  }, [formData.tripType, formData.ambulanceType]);

  const calculatePricing = async () => {
    setIsCalculating(true);
    try {
      // Flat rate pricing
      await new Promise(resolve => setTimeout(resolve, 500));

      // Original pricing (commented for future use):
      // const basePrice = formData.tripType === 'PRIORITY' ? 350 : 250;
      // const emergencySurge = formData.tripType === 'PRIORITY' ? 50 : 0;

      const basePrice = 100; // RM100 flat rate for all ambulance services
      const emergencySurge = 0; // No emergency surcharge
      let ambulanceSurcharge = 0;

      // Only Basic Ambulance is available for now
      if (formData.ambulanceType !== 'BASIC') {
        ambulanceSurcharge = 0; // No surcharge for Basic Ambulance
      }

      const total = basePrice + emergencySurge + ambulanceSurcharge;

      setPricing({
        basePrice,
        emergencySurge,
        total,
        breakdown: `${formData.tripType} hearse service${ambulanceSurcharge > 0 ? ` + Ambulance type surcharge: RM ${ambulanceSurcharge}` : ''}`,
        currency: 'MYR',
      });
    } catch (error) {
      console.error('Failed to calculate pricing:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate required fields
    if (!formData.originAddress.trim()) {
      newErrors.originAddress = 'Pickup address is required';
    }

    if (!formData.destinationAddress.trim()) {
      newErrors.destinationAddress = 'Destination address is required';
    }

    if (formData.onSiteContact.name.trim().length < 2) {
      newErrors.contactName = 'Contact name must be at least 2 characters';
    }

    if (formData.onSiteContact.shareWithProvider && !formData.onSiteContact.phone.trim()) {
      newErrors.contactPhone = 'Phone number is required when sharing contact';
    }

    // Strip display separators (ContactSharingPreferences formats as-you-type)
    if (formData.onSiteContact.phone && !isValidPhone(formData.onSiteContact.phone.replace(/[\s()-]/g, ''))) {
      newErrors.contactPhone = 'Please enter a valid Malaysian or Singaporean phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!pricing) {
      return;
    }

    onSubmit(formData, pricing);
  };

  const handleInputChange = (field: keyof AmbulanceBookingData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleOriginAddressChange = (address: string, coordinates?: { lat: number; lng: number }) => {
    setFormData(prev => ({
      ...prev,
      originAddress: address,
      originCoordinates: coordinates,
    }));

    // Clear error for this field when user starts typing
    if (errors.originAddress) {
      setErrors(prev => ({
        ...prev,
        originAddress: '',
      }));
    }
  };

  const handleDestinationAddressChange = (address: string, coordinates?: { lat: number; lng: number }) => {
    setFormData(prev => ({
      ...prev,
      destinationAddress: address,
      destinationCoordinates: coordinates,
    }));

    // Clear error for this field when user starts typing
    if (errors.destinationAddress) {
      setErrors(prev => ({
        ...prev,
        destinationAddress: '',
      }));
    }
  };

  const ambulanceTypes = [
    { value: 'BASIC', label: 'Basic Hearse', description: 'Standard hearse transport' },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Trip Type Selection */}
      <div>
        <TripTypeSelector
          value={formData.tripType}
          onChange={(value) => handleInputChange('tripType', value)}
          disabled={disabled}
          showPricing={true}
          priorityPrice={100} // RM100 flat rate
          regularPrice={100} // RM100 flat rate
          hidePriority // Temporarily hidden
        />
      </div>

      {/* Location Information */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Pickup & Destination
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Origin Address */}
          <div>
            <label htmlFor="origin-address" className="block text-sm font-medium text-gray-700 mb-1">
              Pickup Address *
            </label>
            <GooglePlacesAutocomplete
              id="origin-address"
              value={formData.originAddress}
              onChange={handleOriginAddressChange}
              disabled={disabled}
              placeholder="Enter pickup address (hospital, home, etc.)"
              error={errors.originAddress}
            />
          </div>

          {/* Destination Address */}
          <div>
            <label htmlFor="destination-address" className="block text-sm font-medium text-gray-700 mb-1">
              Destination Address *
            </label>
            <GooglePlacesAutocomplete
              id="destination-address"
              value={formData.destinationAddress}
              onChange={handleDestinationAddressChange}
              disabled={disabled}
              placeholder="Enter destination address (funeral home, cemetery, etc.)"
              error={errors.destinationAddress}
            />
          </div>
        </div>

        {/* Access Notes */}
        <div className="mt-4">
          <label htmlFor="access-notes" className="block text-sm font-medium text-gray-700 mb-1">
            Access Notes (Optional)
          </label>
          <textarea
            id="access-notes"
            rows={3}
            value={formData.accessNotes}
            onChange={(e) => handleInputChange('accessNotes', e.target.value)}
            disabled={disabled}
            placeholder="Any special instructions for pickup (e.g., parking info, building access, floor number)"
            className={`
              block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500
              border-gray-300 focus:border-blue-500
              ${disabled ? 'bg-gray-50 text-gray-500' : 'bg-white text-gray-900'}
            `}
          />
        </div>
      </div>

      {/* Ambulance Type Selection */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Hearse Type
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ambulanceTypes.map((type) => {
            const isBasicAmbulance = type.value === 'BASIC';
            const isDisabled = disabled || !isBasicAmbulance;

            return (
              <div key={type.value} className="flex items-start space-x-3">
                <div className="flex-shrink-0 pt-1">
                  <input
                    type="radio"
                    name="ambulance-type"
                    value={type.value}
                    checked={formData.ambulanceType === type.value}
                    onChange={() => {
                    if (isBasicAmbulance) {
                      handleInputChange('ambulanceType', type.value);
                    }
                  }}
                    disabled={isDisabled}
                    className={`h-4 w-4 focus:ring-blue-500 border-gray-300 ${
                      isBasicAmbulance ? 'text-blue-600' : 'text-gray-400'
                    }`}
                  />
                </div>
                <div className={`flex-1 ${!isBasicAmbulance ? 'opacity-50' : ''}`}>
                  <label className={`font-medium ${
                    isBasicAmbulance ? 'text-gray-900' : 'text-gray-500'
                  }`}>
                    {type.label}
                  </label>
                  <p className={`text-sm ${
                    isBasicAmbulance ? 'text-gray-600' : 'text-gray-400'
                  }`}>
                    {type.description}
                    {!isBasicAmbulance && ' (Temporarily unavailable)'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Notice about other ambulance types */}
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <p className="text-sm text-yellow-800">
                Only Basic Hearse is currently available. Other hearse types will be available soon.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <ContactSharingPreferences
        contact={formData.onSiteContact}
        onContactChange={(contact) => handleInputChange('onSiteContact', contact)}
        disabled={disabled}
      />

      {/* Special Requests */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Special Requirements (Optional)
        </h3>

        <textarea
          id="special-requests"
          rows={3}
          value={formData.specialRequests}
          onChange={(e) => handleInputChange('specialRequests', e.target.value)}
          disabled={disabled}
          placeholder="Any special medical requirements, equipment needed, or other requests"
          className={`
            block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500
            border-gray-300 focus:border-blue-500
            ${disabled ? 'bg-gray-50 text-gray-500' : 'bg-white text-gray-900'}
          `}
        />
      </div>

      {/* Pricing Summary */}
      {pricing && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Pricing Summary
          </h3>

          {isCalculating ? (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-sm text-gray-600">Calculating pricing...</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Base Price ({formData.tripType})</span>
                <span className="font-medium">RM {pricing.basePrice}</span>
              </div>

              {pricing.emergencySurge > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Emergency Surcharge</span>
                  <span className="font-medium">RM {pricing.emergencySurge}</span>
                </div>
              )}

              {/* Ambulance Type Surcharge removed since only Basic Ambulance is available */}

              <div className="border-t pt-3">
                <div className="flex justify-between">
                  <span className="text-lg font-semibold text-gray-900">Total</span>
                  <span className="text-lg font-semibold text-blue-600">RM {pricing.total}</span>
                </div>
                <p className="mt-1 text-sm text-gray-500">{pricing.breakdown}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Form Actions */}
      <div className="flex justify-end space-x-4 pt-6">
        <button
          type="button"
          onClick={onCancel}
          disabled={disabled}
          className="px-6 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>

        <button
          type="submit"
          disabled={disabled || !pricing || isCalculating}
          className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isCalculating ? 'Calculating...' : 'Create Booking'}
        </button>
      </div>
    </form>
  );
};

export default AmbulanceBookingForm;